import React from 'react'
import { Page } from '../components/Page'
import Step, { StepState } from '../components/Step'
import ChoiceButton from '../components/ChoiceButton'
import { Column, media, Placement, Row, Space } from '@webnuts/shared-components'
import { Checkbox, Dialog, Dropdown, FormikField, TextArea } from '@webnuts/university-greenland-shared-components'
import { Field, Input } from '@webnuts/university-greenland-shared-components'
import { Calendar } from '@webnuts/university-greenland-shared-components'
import { SecondaryButton } from '@webnuts/university-greenland-shared-components'
import { BoxButton } from '@webnuts/university-greenland-shared-components'
import PhoneInput from '../components/PhoneInput'
import styled, { css, ThemeProvider } from 'styled-components'
import { darken } from 'polished'
import withLocale, { LocaleProps } from '../components/Locale'
import IndexContainer, { IndexContainerProps } from '../store/containers/IndexContainer'
import { CommonAppState, domain } from './_app'
import { ErrorMessage, Form, Formik } from 'formik'

import moment from 'moment'
import 'moment/locale/da'
import CenteredCheckbox from '../components/CenteredCheckbox'

type Props = IndexContainerProps & LocaleProps & CommonAppState

type TimeSlot = {
  label: string
}

export type Availability = {
  disabledDates: Date[]
  disabledTimes: string[]
  disableMondays: boolean
  disableTuesdays: boolean
  disableWednesdays: boolean
  disableThursdays: boolean
  disableFridays: boolean
  disableSaturdays: boolean
  disableSundays: boolean
}

export type Location = {
  id: number
  name: string
  availability: Availability
}

export type Education = {
  label: string
  value: string
}

export type Subject = {
  id: number
  name: string
}

enum StepType {
  location = 0,
  subject = 1,
  time = 2,
  information = 3
}

type State = {
  currentStep: StepType
  nextStep?: StepType
  selectedLocation?: Location
  selectedSubject?: Subject
  selectedDate?: Date
  timeSlots: TimeSlot[]
  selectedTimeSlot?: TimeSlot
  educations: Education[]
  firstName: string
  lastName: string
  email: string
  mobile: string
  education?: Education
  message: string
  receiveSmsReminder: boolean
}

const LargeBoxButton = styled(BoxButton)`
  background: white;
  height: 54px;
  padding: 0 32px;
  min-width: 180px;
  ${media('mobile')`
    width: 100%;
  `};
`

const LargeSecondaryButton = styled(SecondaryButton)`
  padding: 19px 18px;
  height: auto;
  color: ${(props) => darken(0.5, props.theme.color.grey.dark)};
  text-transform: none;
  letter-spacing: normal;
  font-size: 14px;
  line-height: 21px;
  transition: background-color 200ms;
  margin-bottom: 15px;
`

const ActiveLargeSecondaryButton = styled(LargeSecondaryButton)`
  background-color: #ebf4fa;

  &:hover {
    background-color: ${() => darken(0.02, '#ebf4fa')};
  }
`

const SmallSecondaryButton = styled(SecondaryButton)`
  display: block;
  flex: 0 0 auto;
  width: calc((100% / 7) - (60px / 7));
  padding-left: 0;
  padding-right: 0;
  margin-right: 10px;
  text-align: center;
  &:nth-child(7n) {
    margin-right: 0;
  }
  ${media('mobile')`
    width: calc(20% - 8px);

    &:nth-child(7n) {
      margin-right: 10px;
    }
    &:nth-child(5n) {
      margin-right: 0!important;
    }
  `};
  text-transform: none;
  letter-spacing: normal;
  font-size: 14px;
  color: ${(props) => darken(0.5, props.theme.color.grey.dark)};
  transition: background-color 200ms;
`

const ActiveSmallSecondaryButton = styled(SmallSecondaryButton)`
  background-color: #ebf4fa;

  &:hover {
    background-color: ${() => darken(0.02, '#ebf4fa')};
  }
`

const AvailableTimeSlots = styled.div`
  text-align: center;
  text-transform: uppercase;
`

const Hint = styled(AvailableTimeSlots)`
  opacity: 0.66;
`

// todo: refactor this to the function method
class Index extends React.Component<Props, State> {
  private static formatDate(date: Date) {
    moment.locale('da')
    return moment(date).format('LL')
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      currentStep: StepType.location,
      nextStep: StepType.subject,
      timeSlots: [],
      educations: window.educations.map((educationString) => ({ label: educationString, value: educationString })),
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      message: '',
      receiveSmsReminder: false
    }

    // This binding is necessary to make `this` work in the callback
    this.handleLocationSelected = this.handleLocationSelected.bind(this)
    this.handleSubjectSelected = this.handleSubjectSelected.bind(this)
    this.handleDateSelected = this.handleDateSelected.bind(this)
    this.handleTimeSlotSelected = this.handleTimeSlotSelected.bind(this)
    this.handleTimeSelected = this.handleTimeSelected.bind(this)
    this.handleReceiveSmsReminderChanged = this.handleReceiveSmsReminderChanged.bind(this)
    this.handleEducationChanged = this.handleEducationChanged.bind(this)
  }

  handleLocationSelected(location: Location) {
    this.setState((prevState, props) => {
      const newState: any = {
        // todo: we should probably not use any here 🤷
        selectedLocation: location,
        currentStep: StepType.subject
      }

      if (prevState.selectedLocation == null) {
        newState.nextStep = StepType.time
      }

      return newState
    })
  }

  handleSubjectSelected(subject: Subject) {
    this.setState((prevState) => {
      // todo: do no use any here
      const newState: any = {
        selectedSubject: subject,
        currentStep: StepType.time,
        selectedDate: undefined,
        selectedTimeSlot: undefined
      }

      if (prevState.selectedSubject == null) {
        newState.nextStep = StepType.information
      }

      return newState
    })
  }

  // todo: fix type
  handleTimeSelected(showDialog: any) {
    if (this.state.selectedDate == null || this.state.selectedTimeSlot == null) {
      showDialog()
    } else {
      this.setState((prevState, props) => {
        const newState: any = {
          // todo: we should probably not use any here 🤷
          currentStep: StepType.information,
          selectedTime: true
        }

        if (prevState.selectedDate == null) {
          newState.nextStep = StepType.information
        }

        return newState
      })

      this.setState({
        // todo: set selected time here
        currentStep: StepType.information,
        nextStep: undefined
      })
    }
  }

  async handleDateSelected(newDate: any) {
    // todo: we should probably not use any here
    this.setState({
      selectedTimeSlot: undefined
    })

    if (this.state.selectedSubject == null || this.state.selectedLocation == null) {
      throw new Error('No selected subject, location or date') // this should never happen
    }

    await fetch(
      `${domain}/umbraco/surface/StudentCounselling/AvailableTimeSlots?subjectId=${this.state.selectedSubject.id.toString()}&location=${
        this.state.selectedLocation.id
      }&date=${moment(newDate.date).format('DD-MM-YYYY')}`
    )
      .then((x) => x.json())
      .then((result) => {
        const newTimeSlots = (result.availableTimes as string[]).map((timeSlotString) => {
          return {
            label: timeSlotString
          }
        })

        this.setState({
          selectedDate: newDate.date,
          timeSlots: newTimeSlots
        })
      })
  }

  handleTimeSlotSelected(newTimeSlot: TimeSlot) {
    // todo: we should probably not use any here
    this.setState({
      selectedTimeSlot: newTimeSlot
    })
  }

  handleReceiveSmsReminderChanged(newValue: boolean) {
    this.setState({
      receiveSmsReminder: newValue
    })
  }

  handleEducationChanged(newValue: Education) {
    this.setState({
      education: newValue
    })
  }

  handleStepNavigate(newStep: StepType) {
    this.setState({
      currentStep: newStep
    })
  }

  stepStateResolver(step: StepType): StepState {
    if (this.state.currentStep === step) {
      return StepState.active
    } else if ((this.state.nextStep || 4) - 1 > step) {
      return StepState.completed
    } else {
      return StepState.inactive
    }
  }

  render() {
    return (
      <Page>
        <Step
          title={this.props.strings('Vælg lokation')}
          headline={this.props.strings('Hvor vil du gerne booke en tid?')}
          label={this.state.selectedLocation ? this.state.selectedLocation.name : undefined}
          state={this.stepStateResolver(StepType.location)}
          onClick={this.handleStepNavigate.bind(this, StepType.location)}
          marker="1"
        >
          <ChoiceButton
            items={this.props.locations}
            selectedItem={this.state.selectedLocation}
            renderItem={(item, selected) => (
              <Space marginRight={15} marginBottom={15}>
                {selected ? (
                  <ActiveLargeSecondaryButton onClick={this.handleLocationSelected.bind(this, item)}>
                    {item.name}
                  </ActiveLargeSecondaryButton>
                ) : (
                  <LargeSecondaryButton onClick={this.handleLocationSelected.bind(this, item)}>
                    {item.name}
                  </LargeSecondaryButton>
                )}
              </Space>
            )}
          />
        </Step>

        <Step
          title={this.props.strings('Vælg emne')}
          headline={this.props.strings('Hvad vil du gerne snakke om?')}
          label={this.state.selectedSubject ? this.props.strings(this.state.selectedSubject.name) : undefined}
          state={this.stepStateResolver(StepType.subject)}
          onClick={this.handleStepNavigate.bind(this, StepType.subject)}
          marker="2"
        >
          <ChoiceButton
            items={this.props.subjects}
            selectedItem={this.state.selectedSubject}
            renderItem={(item, selected) => (
              <Space marginRight={15} marginBottom={15}>
                {selected ? (
                  <ActiveLargeSecondaryButton onClick={this.handleSubjectSelected.bind(this, item)}>
                    {this.props.strings(item.name)}
                  </ActiveLargeSecondaryButton>
                ) : (
                  <LargeSecondaryButton onClick={this.handleSubjectSelected.bind(this, item)}>
                    {this.props.strings(item.name)}
                  </LargeSecondaryButton>
                )}
              </Space>
            )}
          />
        </Step>

        <Step
          title={this.props.strings('Vælg dato og tidspunkt')}
          label={
            // todo: use moment.js here (or some other date modifying tool)
            this.state.selectedDate && this.state.selectedTimeSlot
              ? Index.formatDate(this.state.selectedDate) + ', ' + this.state.selectedTimeSlot.label
              : undefined
          }
          state={this.stepStateResolver(StepType.time)}
          onClick={this.handleStepNavigate.bind(this, StepType.time)}
          marker="3"
        >
          <Row>
            <Column span={6} desktop-span={12} desktop-margin={30}>
              <Calendar
                minDate={new Date()}
                selected={this.state.selectedDate}
                disabledWeekdays={this.props.availableWeekdays(this.state.selectedLocation)}
                isDateDisabled={(date) => this.props.isDateDisabled(date, this.state.selectedLocation)}
                dayNames={['Man', 'Tirs', 'Ons', 'Tors', 'Fre', 'Lør', 'Søn'].map((x) => this.props.strings(x))}
                monthNames={[
                  'Januar',
                  'Februar',
                  'Marts',
                  'April',
                  'Maj',
                  'Juni',
                  'Juli',
                  'August',
                  'September',
                  'October',
                  'November',
                  'December'
                ].map((x) => this.props.strings(x))}
                onDateSelected={this.handleDateSelected}
              />
            </Column>

            <Column span={6} desktop-span={12}>
              {this.state.selectedDate ? (
                <>
                  <AvailableTimeSlots className="grid-gap2-below grid-desktop-gap1-below grid-mobile-gap2-above">
                    {this.props
                      .strings('Ledige tider d. dato')
                      .replace('%dato%', Index.formatDate(this.state.selectedDate))}
                  </AvailableTimeSlots>
                  <ChoiceButton
                    selectedItem={this.state.selectedTimeSlot}
                    items={this.state.timeSlots}
                    renderItem={(item, selected) => (
                      <Space marginRight={10} marginBottom={20}>
                        {selected ? (
                          <ActiveSmallSecondaryButton onClick={this.handleTimeSlotSelected.bind(this, item)}>
                            {item.label}
                          </ActiveSmallSecondaryButton>
                        ) : (
                          <SmallSecondaryButton onClick={this.handleTimeSlotSelected.bind(this, item)}>
                            {item.label}
                          </SmallSecondaryButton>
                        )}
                      </Space>
                    )}
                  />

                  <Dialog
                    zIndex={16000003}
                    content={() => (
                      <Placement horizontal={'center'} vertical={'center'}>
                        {this.props.strings('Du skal vælge både en dato og et tidspunkt')}
                      </Placement>
                    )}
                  >
                    {(showDialog) => (
                      <Space marginTop={40} mobile-marginTop={30}>
                        <Placement horizontal={'center'}>
                          <LargeBoxButton onClick={() => this.handleTimeSelected(showDialog)}>
                            {this.props.strings('Vælg dato og tidspunkt knap')}
                          </LargeBoxButton>
                        </Placement>
                      </Space>
                    )}
                  </Dialog>
                </>
              ) : (
                <Placement horizontal={'center'}>
                  <Hint className="grid-gap2-below grid-desktop-gap1-below grid-mobile-gap2-above">
                    {this.props.strings('Vælg en dato for at se tilgængelige tider')}
                  </Hint>
                </Placement>
              )}
            </Column>
          </Row>
        </Step>

        <Step
          title={this.props.strings('Udfyld information')}
          state={this.stepStateResolver(StepType.information)}
          onClick={this.handleStepNavigate.bind(this, StepType.information)}
          marker="4"
        >
          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              email: '',
              education: '',
              mobile: '',
              message: '',
              sms: false
            }}
            validate={(values) => {
              const errors: any = {}

              const requiredLabel = this.props.strings('Påkrævet')
              const invalidLabel = this.props.strings('Ugyldig')

              if (!values.firstName) {
                errors.firstName = requiredLabel
              }

              if (!values.lastName) {
                errors.lastName = requiredLabel
              }

              if (!values.email) {
                errors.email = requiredLabel
              }

              if (!values.mobile) {
                errors.mobile = requiredLabel
              } else if (!window.$('[name=mobile]').intlTelInput('isValidNumber')) {
                errors.mobile = invalidLabel
              }

              if (this.state.education == null) {
                errors.education = requiredLabel
              }

              return errors
            }}
            onSubmit={async (values, { setSubmitting }) => {
              if (
                this.state.selectedTimeSlot == null ||
                this.state.selectedSubject == null ||
                this.state.education == null ||
                this.state.selectedLocation == null
              ) {
                throw new Error('Something went wrong') // this should never happen
              }

              const url = new URL(`${domain || location.origin}/umbraco/surface/StudentCounselling/CreateBooking`)

              const payload: { [s: string]: string } = {
                date: moment(this.state.selectedDate).format('DD-MM-YYYY'),
                time: this.state.selectedTimeSlot.label,
                firstname: values.firstName,
                lastname: values.lastName,
                email: values.email,
                mobileNumber: window.$('[name=mobile]').intlTelInput('getNumber') || values.mobile,
                education: this.state.education.label,
                message: values.message,
                sms: this.state.receiveSmsReminder ? '1' : '0',
                subjectId: this.state.selectedSubject.id.toString(),
                pageId: (window.pageId || 44459).toString(),
                location: this.state.selectedLocation.id.toString()
              }

              Object.keys(payload).forEach((key) => url.searchParams.append(key, payload[key]))

              const result = await fetch(url.toString(), {
                method: 'POST'
              }).finally(() => setSubmitting(false))

              await result.json().then((submitResult) => {
                window.location.href = domain + submitResult.redirectUrl
              })
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
              <Form>
                <Row columnMargin={25}>
                  <Column span={6} desktop-span={12}>
                    <FormikField
                      type={'text'}
                      name={'firstName'}
                      placeholder={this.props.strings('Indtast fornavn')}
                      label={this.props.strings('Fornavn')}
                      component={Input}
                    />
                  </Column>

                  <Column span={6} desktop-span={12}>
                    <FormikField
                      type={'text'}
                      name={'lastName'}
                      placeholder={this.props.strings('Indtast efternavn')}
                      label={this.props.strings('Efternavn')}
                      component={Input}
                    />
                  </Column>

                  <Column span={6} desktop-span={12}>
                    <FormikField
                      type={'email'}
                      name={'email'}
                      placeholder={this.props.strings('Indtast email')}
                      label={this.props.strings('Email')}
                      component={Input}
                    />
                  </Column>

                  <Column span={6} desktop-span={12}>
                    <FormikField
                      name={'education'}
                      label={this.props.strings('Uddannelse')}
                      placeholder={this.props.strings('Vælg uddannelse')}
                      defaultValue={{ value: '', label: this.props.strings('Vælg') }}
                      component={(props: any) => <Dropdown {...props} onChange={this.handleEducationChanged} />}
                      value={this.state.education && this.state.education.value}
                      choices={this.state.educations}
                    />
                  </Column>

                  <Column span={6} desktop-span={12}>
                    <PhoneInput id="mobile">
                      <FormikField
                        type={'tel'}
                        name={'mobile'}
                        placeholder={this.props.strings('Indtast mobil')}
                        label={this.props.strings('Mobil nr.')}
                        component={Input}
                      />
                    </PhoneInput>
                  </Column>

                  <Column span={6} desktop-span={12}>
                    <FormikField
                      type={'checkbox'}
                      name={'sms'}
                      value={this.state.receiveSmsReminder}
                      label={this.props.strings('Skal vi sende en SMS som reminder?')}
                      onChange={this.handleReceiveSmsReminderChanged}
                      hint={this.props.strings('Ja tak – send mig SMS dagen før')}
                      component={CenteredCheckbox}
                    />
                  </Column>

                  <Column span={12}>
                    <FormikField
                      type={'textarea'}
                      name={'message'}
                      label={this.props.strings('Besked')}
                      placeholder={this.props.strings('Her kan du evt. skrive en besked til studievejlederen')}
                      component={TextArea}
                    />
                  </Column>

                  <Column span={12} margin={0}>
                    <LargeBoxButton type={'submit'} disabled={isSubmitting}>
                      {this.props.strings('Book tid')}
                    </LargeBoxButton>
                  </Column>
                </Row>
              </Form>
            )}
          </Formik>
        </Step>
      </Page>
    )
  }
}

// export default Index

export default IndexContainer(withLocale(Index))
